/**
 * Chords as a guitarist holds them: one note per string, six strings low to high, in standard
 * tuning. An open shape where the chord has a common one, otherwise a barre on the E or the A shape.
 *
 * A leaf: it reads chord symbols and nothing about rhythm.
 */

import { getChordNotes } from './chordNotes';
import { getPitchClass } from './chordVoicing';

/** The open strings, low E to high E */
export const GUITAR_TUNING = [40, 45, 50, 55, 59, 64];

// The highest fret a slash chord's bass is reached for on the two low strings
const BASS_REACH = 5;

// What a chord's tones are, as a key: its intervals folded into one octave. An add9 reads as an add2.
const toKey = (intervals) => [...new Set(intervals.map((interval) => interval % 12))].sort((a, b) => a - b).join(',');

const MAJOR = '0,4,7';
const MINOR = '0,3,7';
const DOM7 = '0,4,7,10';
const MIN7 = '0,3,7,10';
const MAJ7 = '0,4,7,11';
const SUS4 = '0,5,7';
const SUS2 = '0,2,7';
const ADD2 = '0,2,4,7';

// The open shapes, by root pitch class, written as a guitarist reads them: a fret per string, x muted
const OPEN_SHAPES = {
  0: { [MAJOR]: 'x32010', [DOM7]: 'x32310', [MAJ7]: 'x32000', [ADD2]: 'x32030' },
  2: {
    [MAJOR]: 'xx0232',
    [MINOR]: 'xx0231',
    [DOM7]: 'xx0212',
    [MIN7]: 'xx0211',
    [MAJ7]: 'xx0222',
    [SUS4]: 'xx0233',
    [SUS2]: 'xx0230',
  },
  4: { [MAJOR]: '022100', [MINOR]: '022000', [DOM7]: '020100', [MIN7]: '022030', [MAJ7]: '021100', [SUS4]: '022200' },
  5: { [MAJ7]: 'xx3210' },
  // The worship G, with the D doubled on the B string, which rings into C, D and Em the same way
  7: { [MAJOR]: '320033', [DOM7]: '320001', [MAJ7]: '320002' },
  9: {
    [MAJOR]: 'x02220',
    [MINOR]: 'x02210',
    [DOM7]: 'x02020',
    [MIN7]: 'x02010',
    [MAJ7]: 'x02120',
    [SUS4]: 'x02230',
    [SUS2]: 'x02200',
  },
  11: { [DOM7]: 'x21202' },
};

const fromFrets = (frets) =>
  [...frets].map((fret, string) => (fret === 'x' ? null : GUITAR_TUNING[string] + Number(fret)));

// What each string of a barre shape plays, from its root string up: R root, 5 fifth, 3 third, 7 the
// octave root a seventh or sixth takes the place of
const BARRE_ROLES = {
  E: { string: 0, roles: ['R', '5', '7', '3', '5', 'R'], octaves: [0, 0, 1, 1, 1, 2] },
  A: { string: 1, roles: ['R', '5', '7', '3', '5'], octaves: [0, 0, 1, 1, 1] },
};

// The fret a root sits at on a string
const fretOf = (pitchClass, string) => (pitchClass - (GUITAR_TUNING[string] % 12) + 12) % 12;

// The E shape or the A shape, whichever puts the root lower on the neck
const pickBarre = (rootClass) => (fretOf(rootClass, 1) < fretOf(rootClass, 0) ? BARRE_ROLES.A : BARRE_ROLES.E);

// The first of these the chord has, so an add2 keeps its third rather than reading as a sus2
const firstOf = (candidates, intervals) => candidates.find((interval) => intervals.includes(interval));

const barreShape = (rootClass, intervals) => {
  const shape = pickBarre(rootClass);
  const low = GUITAR_TUNING[shape.string] + fretOf(rootClass, shape.string);
  const seventh = firstOf([10, 11, 9], intervals);
  // A seventh or sixth sits under the octave root it replaces
  const offsets = {
    R: 0,
    5: firstOf([7, 6, 8], intervals) ?? 7,
    3: firstOf([4, 3, 2, 5], intervals),
    7: seventh === undefined ? 0 : seventh - 12,
  };

  return [
    ...Array(shape.string).fill(null),
    // A power chord has no third, so that string is left out rather than doubled
    ...shape.roles.map((role, index) =>
      offsets[role] === undefined ? null : low + 12 * shape.octaves[index] + offsets[role],
    ),
  ];
};

// The low strings that sound, the lowest first
const lowestSounding = (shape) => shape.findIndex((midi) => midi !== null);

/**
 * Puts a slash chord's bass on the 6th or 5th string, low on the neck and under everything else
 * the shape plays. Out of reach, the shape's own lowest string is muted and the bass left to the
 * bass player.
 */
const withBass = (shape, bassClass) => {
  const placed = [...shape];
  // The lower fret first: F/A takes the open A string, not the 6th string's fifth fret
  const strings = [0, 1].sort((a, b) => fretOf(bassClass, a) - fretOf(bassClass, b));

  for (const string of strings) {
    const fret = fretOf(bassClass, string);
    const midi = GUITAR_TUNING[string] + fret;
    const above = placed.slice(string + 1).filter((note) => note !== null);

    if (fret <= BASS_REACH && above.length > 0 && midi < Math.min(...above)) {
      placed[string] = midi;
      if (string === 1) placed[0] = null;

      return placed;
    }
  }

  placed[lowestSounding(placed)] = null;

  return placed;
};

/**
 * A chord as six strings, low E to high E: a MIDI note each, or null for a string not played.
 * Anything past the seventh is left to the piano.
 *
 * @param {string} chord - A chord symbol
 * @returns {Array<number|null>} six entries, or [] for a chord that can't be read
 */
export const getGuitarShape = (chord) => {
  const { root, intervals, bassNote, inversion } = getChordNotes(chord);
  const rootClass = getPitchClass(root);

  if (rootClass === null) return [];

  const frets = OPEN_SHAPES[rootClass]?.[toKey(intervals)];
  const shape = frets ? fromFrets(frets) : barreShape(rootClass, intervals);
  // An inversion asks for its chord tone in the bass, as a slash chord does
  const bassClass = bassNote
    ? getPitchClass(bassNote)
    : inversion > 0
      ? (rootClass + intervals[inversion % intervals.length]) % 12
      : null;

  return bassClass === null || bassClass === rootClass ? shape : withBass(shape, bassClass);
};

/**
 * A power chord in the barre shape's place: the root on the 6th or the 5th string, its fifth and
 * its octave on the two strings above, the rest left out.
 *
 * @param {string} chord - A chord symbol
 * @returns {Array<number|null>} six entries, or [] for a chord that can't be read
 */
export const getPowerShape = (chord) => {
  const { root, intervals } = getChordNotes(chord);
  const rootClass = getPitchClass(root);

  if (rootClass === null) return [];

  const { string } = pickBarre(rootClass);
  const low = GUITAR_TUNING[string] + fretOf(rootClass, string);
  const fifth = firstOf([7, 6, 8], intervals) ?? 7;

  return GUITAR_TUNING.map((_, index) => [low, low + fifth, low + 12][index - string] ?? null);
};
